import "./styles.scss";

import { Link, useParams } from "react-router-dom";
import classnames from "classnames";

import Tooltip from "components/ToolTip";
import DetailsCardCard from "./components/Card";
import DetailsCardStatus from "./components/Status";
import DetailsCardSwitch from "./components/Switch";
import DetailsCardAvatar from "./components/Avatar";
import PaymentError from "./components/PaymentError";
import DetailsCardLanguage from "./components/Language";
import DetailsCardWebmaster from "./components/Webmaster";
import ChangePlanPreview from "./components/ChangePlanPreview";
import DetailsCardWebmasterTime from "./components/WebmasterTime";
import DetailsCardAdvice from "./components/Advice";
import RadioButtons from "components/RadioButtons";
import Button from "components/Button";

import useResponsive from "libs/helpers/useResponsive";
import task_status_data from "libs/task_status_data.json";

import { ReactComponent as QuestionIcon } from "assets/icons/new/question-icon.svg";
import { ReactComponent as ArrowIcon } from "assets/icons/new/forward-arrow.svg";

const mainClass = "details-card-list";

const details_value_type = (value, type, onclick) => {
  switch (type) {
    case "status":
      return <DetailsCardStatus value={value?.status} />;
    case "card":
      return <DetailsCardCard value={value} />;
    case "switch":
      return <DetailsCardSwitch onClick={onclick} value={value} />;
    case "change_plan_preview":
      return <ChangePlanPreview value={value} />;
    case "button":
      const isExternal = value?.external_href;
      const isButton = value?.onclick;
      const Tag = isButton ? Button : isExternal ? "a" : Link;

      return (
        <Tag
          to={!isButton && !isExternal ? value?.button_href : undefined}
          href={!isButton ? value?.external_href : undefined}
          target={!isButton && isExternal ? "_blank" : undefined}
          rel={
            !isButton && isExternal ? "noopener noreferrer nofollow" : undefined
          }
          onClick={value?.onclick}
        >
          {value?.button_label}
          <i>
            <ArrowIcon />
          </i>
        </Tag>
      );
    case "link":
      return <Link to={value?.link_href}>{value?.link_label}</Link>;
    case "link_external":
      return (
        <a target="_blank" rel="noopener noreferrer" href={value?.link_href}>
          {value?.link_label}
        </a>
      );
    case "avatar":
      return <DetailsCardAvatar value={value} />;
    case "webmaster":
      return !!value?.name ? <DetailsCardWebmaster value={value} /> : null;
    case "webmaster_time":
      return <DetailsCardWebmasterTime value={value} onClick={onclick} />;
    case "language":
      return <DetailsCardLanguage value={value} />;
    case "advice":
      return <DetailsCardAdvice value={value} />;
    case "radio":
      return <RadioButtons options={value} />;
    default:
      return value;
  }
};

const DetailsCardList = ({
  variant,
  websiteId,
  items,
  view,
  clientReturnQuery,
}) => {
  const { max } = useResponsive();
  const { id } = useParams();

  return view === "payment_error" ? (
    <PaymentError items={items} />
  ) : items?.length > 0 ? (
    <div
      className={classnames(mainClass, {
        [`${mainClass}--${variant}`]: !!variant,
      })}
    >
      {items?.map(
        (
          {
            label,
            value,
            custom_children,
            status,
            type,
            tooltip,
            onclick,
            active = "true",
            align,
            hiddenMobile = false,
            hiddenDesktop = false,
          },
          index,
        ) => {
          if (
            !!!active ||
            (max.sm && hiddenMobile) ||
            (!max.sm && hiddenDesktop)
          )
            return null;

          const labelItems = items?.filter((item) => !!item.label) ?? [];
          const labelLessItems =
            items?.slice(0, index).filter((item) => !item.label) ?? [];
          const isOdd = !!label && (index - labelLessItems.length) % 2 !== 0;

          const isMobileTask = variant === "tasks" && max.sm;
          const isTaskLink = isMobileTask && Number(value) > 0;
          const Item = isTaskLink ? Link : "div";
          const taskStatus = task_status_data.find(
            (item) => item.value === status,
          )?.key;

          return (
            <Item
              key={index}
              to={
                isTaskLink
                  ? `/clients/${websiteId || id}/tasks?status=${taskStatus}${
                      clientReturnQuery ? `&${clientReturnQuery}` : ""
                    }`
                  : undefined
              }
              className={
                isMobileTask
                  ? classnames(`${mainClass}__item`, {
                      [`${mainClass}__item--status-${status}`]: !!status,
                    })
                  : classnames(`${mainClass}__item`, {
                      row: !!label && labelItems.length > 1,
                      "row-first":
                        !!label && index - labelLessItems.length === 0,
                      "row-last":
                        !!label &&
                        index - labelLessItems.length === labelItems.length - 1,
                      "row-odd": isOdd,
                      "full-width": type === "switch",
                      "no-value": !!!value && value,
                    })
              }
            >
              {custom_children}
              {!!label && (
                <label className={classnames({ [`align-${align}`]: !!align })}>
                  {label}
                </label>
              )}
              {!custom_children && (
                <span
                  className={classnames({
                    "flex-end": type === "switch",
                  })}
                >
                  {details_value_type(value, type, onclick)}
                  {!!tooltip && (
                    <Tooltip text={tooltip}>
                      <QuestionIcon />
                    </Tooltip>
                  )}
                </span>
              )}
            </Item>
          );
        },
      )}
    </div>
  ) : null;
};

export default DetailsCardList;
